import React, { useState, useMemo, useRef, useEffect } from 'react';
import { GoogleGenAI, Type } from "@google/genai";
import { type Room, type Tenant, type RoomMember, type ResidencyRegistration, type BoardingHouseInfo, RoomStatus, ResidencyStatus } from '../types';
import { ELECTRICITY_PRICE } from '../constants';
import Icon from './ui/Icon';
import ConfirmationModal from './ui/ConfirmationModal';
import ContractModal from './ContractModal';
import CT01Modal from './CT01Modal';
import { formatDateForInput } from '../utils/dateUtils';

declare const html2canvas: any;
declare const jspdf: any;

interface RoomManagementProps {
    rooms: Room[];
    setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
    tenants: Tenant[];
    setTenants: React.Dispatch<React.SetStateAction<Tenant[]>>;
    roomMembers: RoomMember[];
    setRoomMembers: React.Dispatch<React.SetStateAction<RoomMember[]>>;
    residencyRegistrations: ResidencyRegistration[];
    setResidencyRegistrations: React.Dispatch<React.SetStateAction<ResidencyRegistration[]>>;
    allPeople: any[];
    geminiApiKey: string;
    boardingHouseInfo: BoardingHouseInfo;
}

const numberToVnWords = (number: number): string => {
    if (number === 0) return "Không đồng";
    if (number > 999999999999999) return "Số quá lớn";

    const units = ["", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
    const teens = ["mười", "mười một", "mười hai", "mười ba", "mười bốn", "mười lăm", "mười sáu", "mười bảy", "mười tám", "mười chín"];
    const tens = ["", "mười", "hai mươi", "ba mươi", "bốn mươi", "năm mươi", "sáu mươi", "bảy mươi", "tám mươi", "chín mươi"];
    const thousands = ["", "nghìn", "triệu", "tỷ"];

    const convertGroup = (n: number): string => {
        let result = "";
        const hundred = Math.floor(n / 100);
        const ten = n % 100;
        
        if (hundred > 0) {
            result += units[hundred] + " trăm";
        }

        if (ten > 0) {
            if (hundred > 0) {
                result += ten < 10 ? " linh " : " ";
            }
            if (ten < 10) {
                result += units[ten];
            } else if (ten < 20) {
                result += teens[ten - 10];
            } else {
                result += tens[Math.floor(ten / 10)];
                const unit = ten % 10;
                if (unit > 0) {
                    if (unit === 1 && Math.floor(ten/10) > 1) {
                        result += " mốt";
                    } else if (unit === 5 && Math.floor(ten/10) > 0) {
                        result += " lăm";
                    } else if (unit === 4 && Math.floor(ten/10) > 1) {
                        result += " tư";
                    } else {
                        result += " " + units[unit];
                    }
                }
            }
        }
        return result;
    }

    const numStr = number.toString();
    const groups: string[] = [];
    for (let i = numStr.length; i > 0; i -= 3) {
        groups.push(numStr.substring(Math.max(0, i - 3), i));
    }

    const words: string[] = [];
    for (let i = 0; i < groups.length; i++) {
        const groupNum = parseInt(groups[i], 10);
        if (groupNum > 0) {
            let groupWord = convertGroup(groupNum);
            if (i > 0) {
                 groupWord += " " + thousands[i];
            }
            words.push(groupWord);
        }
    }
    
    let finalResult = words.reverse().join(" ");
    finalResult = finalResult.trim().replace(/\s+/g, ' ');
    finalResult = finalResult.charAt(0).toUpperCase() + finalResult.slice(1);

    return finalResult + " đồng chẵn.";
};


const InvoiceModal: React.FC<{ room: Room | null; tenantName: string; onClose: () => void; boardingHouseInfo: BoardingHouseInfo; }> = ({ room, tenantName, onClose, boardingHouseInfo }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    if (!room) return null;

    const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
    const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
    const totalBill = room.price + electricityCost;
    const today = new Date();
    const invoiceDate = `Ngày ${today.getDate()} tháng ${today.getMonth() + 1} năm ${today.getFullYear()}`;
    const totalBillInWords = numberToVnWords(totalBill);
    const invoiceId = `HD-${room.roomNumber}-${today.getFullYear()}${(today.getMonth() + 1).toString().padStart(2, '0')}`;


    const handlePrint = () => { window.print(); };

    const handleDownloadPdf = () => {
        const content = document.getElementById('invoice-content-to-download');
        if (!content) return;
        setIsDownloading(true);

        const { jsPDF } = jspdf;
        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
            hotfixes: ["px_scaling"],
        });

        // Embed the Noto Serif font to support Vietnamese characters
        if ((window as any).setupPdfFonts) {
            (window as any).setupPdfFonts(pdf);
        }
        pdf.setFont('Noto Serif', 'normal');

        pdf.html(content, {
            callback: (doc: any) => {
                doc.save(`Hoa-Don-Thang-${today.getMonth() + 1}-${room.roomNumber}-${tenantName.replace(/\s/g, '_')}.pdf`);
                setIsDownloading(false);
            },
            margin: [20, 20, 20, 20],
            autoPaging: 'text',
            width: 170,
            windowWidth: content.scrollWidth,
             html2canvas: {
                useCORS: true,
                scale: window.devicePixelRatio,
            },
            fontFaces: [
                {
                    family: 'Noto Serif',
                    style: 'normal',
                    weight: 'normal',
                    src: [{ url: 'NotoSerif-Regular.ttf', format: 'truetype' }]
                },
                {
                    family: 'Noto Serif',
                    style: 'normal',
                    weight: 'bold',
                    src: [{ url: 'NotoSerif-Bold.ttf', format: 'truetype' }]
                }
            ]
        }).catch((error: any) => {
            import('../utils/logger').then(l => l.error('Error generating PDF:', error));
            alert("Đã có lỗi xảy ra khi tạo file PDF.");
            setIsDownloading(false);
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 no-print">
            <div id="invoice-modal" className="printable-content bg-white dark:bg-gray-800 p-8 rounded-lg shadow-2xl w-full max-w-3xl mx-auto text-gray-800 dark:text-gray-200 h-[90vh] overflow-y-auto">
                <div className="printable-document bg-white p-4">
                    <div id="invoice-content-to-download">
                        <div className="text-center mb-4">
                            <h3 className="font-bold uppercase">{boardingHouseInfo.name}</h3>
                            <p>Địa chỉ: {boardingHouseInfo.address}</p>
                            <p>ĐT: {boardingHouseInfo.phone}</p>
                        </div>
                        <div className="text-center my-6">
                            <h2 className="text-2xl font-bold">HÓA ĐƠN TIỀN NHÀ</h2>
                            <p>Tháng {today.getMonth() + 1} năm {today.getFullYear()}</p>
                            <p className="italic mt-1">Số: {invoiceId}</p>
                        </div>

                        <div className="mb-6">
                            <p><span className="font-semibold">Đơn vị thuê:</span> {tenantName}</p>
                            <p><span className="font-semibold">Phòng số:</span> {room.roomNumber}</p>
                        </div>

                        <table className="w-full border-collapse border border-gray-400">
                            <thead>
                                <tr className="">
                                    <th className="border border-gray-300 p-2 text-center font-semibold">STT</th>
                                    <th className="border border-gray-300 p-2 text-left font-semibold">Nội dung</th>
                                    <th className="border border-gray-300 p-2 text-right font-semibold">Thành tiền (VNĐ)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-gray-300 p-2 text-center">1</td>
                                    <td className="border border-gray-300 p-2">
                                        Tiền thuê phòng tháng {today.getMonth() + 1}/{today.getFullYear()}
                                        <p className="text-gray-500 italic">Bao gồm các chi phí dịch vụ cơ bản (nước, rác, internet).</p>
                                    </td>
                                    <td className="border border-gray-300 p-2 text-right">{room.price.toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2 text-center">2</td>
                                    <td className="border border-gray-300 p-2">
                                        Tiền điện
                                        <p className="text-gray-500 italic">
                                            (Số mới: {room.currentElectricityReading} - Số cũ: {room.lastElectricityReading} = {electricityUsage} kWh) x {ELECTRICITY_PRICE.toLocaleString()} VNĐ/kWh
                                        </p>
                                    </td>
                                    <td className="border border-gray-300 p-2 text-right">{electricityCost.toLocaleString()}</td>
                                </tr>
                                <tr className="font-bold">
                                    <td colSpan={2} className="border border-gray-300 p-2 text-right">TỔNG CỘNG THANH TOÁN</td>
                                    <td className="border border-gray-300 p-2 text-right text-lg">{totalBill.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>
                        
                        <div className="mt-2">
                            <p><span className="font-semibold">Bằng chữ:</span> <span className="italic">{totalBillInWords}</span></p>
                        </div>

                        <div className="flex justify-around pt-12 text-center">
                            <div>
                                <h3 className="font-bold">Người nộp tiền</h3>
                                <p className="italic">(Ký và ghi rõ họ tên)</p>
                                <div className="h-24"></div>
                                <p className="font-semibold">{tenantName}</p>
                            </div>
                            <div>
                                <p className="italic mb-2">{invoiceDate}</p>
                                <h3 className="font-bold">Người lập phiếu</h3>
                                <p className="italic">(Ký và ghi rõ họ tên)</p>
                                <div className="h-24"></div>
                                <p className="font-semibold">{boardingHouseInfo.landlordName}</p>
                            </div>
                        </div>
                         <div className="text-center text-gray-500 pt-8 mt-8 border-t border-gray-300">
                            <p>© 2025 IVS JSC. Mọi quyền được bảo lưu.</p>
                            <p className="mt-1">Được thiết kế và phát triển bởi IVS Celestech, IVS JSC.</p>
                            <a href="https://ivsacademy.edu.vn/" target="_blank" rel="noopener noreferrer" className="mt-1 block text-primary-600 dark:text-primary-400 hover:underline">
                                https://ivsacademy.edu.vn/
                            </a>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-0 sm:space-x-4 mt-8 no-print">
                    <button onClick={onClose} className="px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Đóng</button>
                    <button
                        onClick={handleDownloadPdf}
                        disabled={isDownloading}
                        className="px-4 py-2 font-medium text-white bg-green-600 rounded-lg hover:bg-green-500 disabled:bg-green-300"
                    >
                        {isDownloading ? 'Đang xử lý...' : 'Tải PDF'}
                    </button>
                    <button onClick={handlePrint} className="px-4 py-2 font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500">In Hóa Đơn</button>
                </div>
            </div>
        </div>
    );
};

const SelectPersonModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    people: any[];
    onSelect: (person: any) => void;
    onAddNew: () => void;
}> = ({ isOpen, onClose, people, onSelect, onAddNew }) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!isOpen) return null;

    const filteredPeople = people.filter(p => 
        (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.idNumber && p.idNumber.includes(searchTerm))
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[60]">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Chọn người từ hệ thống</h3>
                <input 
                    type="text"
                    placeholder="Tìm theo tên hoặc số CMND/CCCD..."
                    className="w-full p-2 border rounded mb-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <div className="max-h-60 overflow-y-auto mb-4 border dark:border-gray-600 rounded-md">
                    {filteredPeople.map(person => (
                        <div key={person.idNumber} onClick={() => onSelect(person)} className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b dark:border-gray-600">
                            <p className="font-semibold text-gray-800 dark:text-white">{person.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{person.idType}: {person.idNumber}</p>
                        </div>
                    ))}
                    {filteredPeople.length === 0 && <p className="p-3 text-center text-gray-500 dark:text-gray-400">Không tìm thấy người phù hợp.</p>}
                </div>
                <div className="flex justify-between items-center">
                    <button onClick={onAddNew} className="px-4 py-2 font-medium text-white bg-green-600 rounded-lg hover:bg-green-500">
                        Thêm thành viên mới
                    </button>
                    <button onClick={onClose} className="px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                        Hủy
                    </button>
                </div>
            </div>
        </div>
    );
};

const ManageRoomModal: React.FC<{
    room: Room;
    rooms: Room[]; // Needed for validation
    tenants: Tenant[];
    roomMembers: RoomMember[];
    residencyRegistrations: ResidencyRegistration[];
    onClose: () => void;
    onSave: (payload: { room: Room, tenant?: Tenant, members: RoomMember[], deletedMembers: string[], isNewTenant: boolean, residency?: ResidencyRegistration }) => void;
    onClearOccupant: (roomId: string, tenantId: string) => void;
    onClearOccupantAndUpdateRoom: (roomId: string, tenantId: string, updatedRoomData: { status: RoomStatus, price: number, roomNumber: string }) => void;
    allPeople: any[];
    geminiApiKey: string;
    boardingHouseInfo: BoardingHouseInfo;
}> = ({ room, rooms, tenants, roomMembers, residencyRegistrations, onClose, onSave, onClearOccupant, onClearOccupantAndUpdateRoom, allPeople, geminiApiKey, boardingHouseInfo }) => {
    const [activeTab, setActiveTab] = useState('tenant');
    const [roomPrice, setRoomPrice] = useState(room.price);
    const [roomNumber, setRoomNumber] = useState(room.roomNumber);
    const [roomStatus, setRoomStatus] = useState(room.status);
    const [lastReading, setLastReading] = useState(room.lastElectricityReading);
    const [currentReading, setCurrentReading] = useState(room.currentElectricityReading);
    const [tenantData, setTenantData] = useState<Omit<Tenant, 'id' | 'roomId'>>({
        name: '', gender: 'Nam', idType: 'CCCD', phone: '', idNumber: '', permanentAddress: '', dateOfBirth: '', idIssueDate: '',
        contractStartDate: new Date().toISOString().split('T')[0],
        contractEndDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        documentUrl: '',
    });
    const [residencyData, setResidencyData] = useState<Omit<ResidencyRegistration, 'id' | 'personId'>>({
        temporaryAddress: `Phòng ${room.roomNumber}, ${boardingHouseInfo.name}, ${boardingHouseInfo.address}`,
        arrivalDate: new Date().toISOString().split('T')[0],
        departureDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        status: ResidencyStatus.Unregistered,
    });
    const [currentTenantId, setCurrentTenantId] = useState<string | undefined>(room.tenantId);
    const [members, setMembers] = useState<RoomMember[]>([]);
    const [deletedMembers, setDeletedMembers] = useState<string[]>([]);
    const [isNewTenant, setIsNewTenant] = useState(!room.tenantId);
    const [expandedMember, setExpandedMember] = useState<string | null>(null);
    const [isSelectPersonModalOpen, setIsSelectPersonModalOpen] = useState(false);
    
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionError, setExtractionError] = useState<string | null>(null);
    const hasApiKey = geminiApiKey && geminiApiKey.trim() !== '';

    // Confirmation Modal State
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    const [confirmProps, setConfirmProps] = useState({ title: '', message: '', onConfirm: () => {} });

    useEffect(() => {
        const tenant = tenants.find(t => t.id === room.tenantId);
        if (tenant) {
            setTenantData(tenant);
            setCurrentTenantId(tenant.id);
            setIsNewTenant(false);
            setMembers(roomMembers.filter(m => m.tenantId === tenant.id));
            
            const registration = residencyRegistrations.find(r => r.personId === tenant.id);
            if (registration) {
                setResidencyData(registration);
            } else {
                setResidencyData({
                    temporaryAddress: `Phòng ${room.roomNumber}, ${boardingHouseInfo.name}, ${boardingHouseInfo.address}`,
                    arrivalDate: tenant.contractStartDate,
                    departureDate: tenant.contractEndDate ?? tenant.contractStartDate ?? new Date().toISOString().split('T')[0],
                    status: ResidencyStatus.Unregistered,
                });
            }
        } else {
            setIsNewTenant(true);
            if (boardingHouseInfo.defaultRoomPrice > 0 && room.status !== RoomStatus.Occupied) {
                setRoomPrice(boardingHouseInfo.defaultRoomPrice);
            }
        }
        setExtractionError(null);
    }, [room, tenants, roomMembers, residencyRegistrations, boardingHouseInfo]);

    const selectablePeople = useMemo(() => {
        const currentPeopleIds = new Set(members.map(m => m.idNumber).filter(Boolean));
        if (tenantData.idNumber) {
            currentPeopleIds.add(tenantData.idNumber);
        }
        return allPeople.filter(p => p.idNumber && !currentPeopleIds.has(p.idNumber));
    }, [allPeople, members, tenantData.idNumber]);

    const handleSelectPerson = (person: any) => {
        const newMember: RoomMember = {
            id: `M${Date.now()}`,
            tenantId: currentTenantId || `T_new_${Date.now()}`,
            name: person.name,
            gender: person.gender,
            idType: person.idType,
            idNumber: person.idNumber,
            phone: person.phone,
            permanentAddress: person.permanentAddress,
            dateOfBirth: person.dateOfBirth,
            idIssueDate: person.idIssueDate,
            relationship: ''
        };
        setMembers(prev => [...prev, newMember]);
        setExpandedMember(newMember.id);
        setIsSelectPersonModalOpen(false);
    };

    const handleAddNewBlankMember = () => {
        const newMember: RoomMember = {
            id: `M${Date.now()}`,
            tenantId: currentTenantId || `T_new_${Date.now()}`,
            name: '', gender: 'Nam', idType: 'CCCD', idNumber: '', phone: '',
            permanentAddress: '', dateOfBirth: '', idIssueDate: '', relationship: ''
        };
        setMembers(prev => [...prev, newMember]);
        setExpandedMember(newMember.id);
        setIsSelectPersonModalOpen(false);
    };

    
    const handleIdCardUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsExtracting(true);
        setExtractionError(null);

        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64String = (reader.result as string).split(',')[1];
                
                const ai = new GoogleGenAI({ apiKey: geminiApiKey });
                
                const responseSchema = {
                  type: Type.OBJECT,
                  properties: {
                    idNumber: { type: Type.STRING, description: "Số Căn cước công dân" },
                    fullName: { type: Type.STRING, description: "Họ và tên đầy đủ, viết hoa không dấu" },
                    dateOfBirth: { type: Type.STRING, description: "Ngày sinh theo định dạng dd/mm/yyyy" },
                    gender: { type: Type.STRING, description: "Giới tính, 'Nam' hoặc 'Nữ'" },
                    permanentAddress: { type: Type.STRING, description: "Địa chỉ nơi thường trú" },
                    idIssueDate: { type: Type.STRING, description: "Ngày cấp thẻ, theo định dạng dd/mm/yyyy. Nếu không tìm thấy, trả về chuỗi rỗng." },
                  },
                  required: ["idNumber", "fullName", "dateOfBirth", "gender", "permanentAddress", "idIssueDate"]
                };

                const response = await ai.models.generateContent({
                    model: "gemini-2.5-flash",
                    contents: {
                        parts: [
                            { inlineData: { mimeType: 'image/jpeg', data: base64String } },
                            { text: "Trích xuất thông tin từ Căn cước công dân này. Họ và tên phải được chuyển thành chữ không dấu." }
                        ]
                    },
                    config: {
                        responseMimeType: "application/json",
                        responseSchema: responseSchema,
                    }
                });

                    const text = typeof response.text === 'string' ? response.text : (response.text ?? '');
                    if (!text) throw new Error('Empty response from OCR');
                    const extractedData = JSON.parse(text);
                
                setTenantData(prev => ({
                    ...prev,
                    name: extractedData.fullName || prev.name,
                    idNumber: extractedData.idNumber || prev.idNumber,
                    dateOfBirth: formatDateForInput(extractedData.dateOfBirth) || prev.dateOfBirth,
                    gender: (extractedData.gender === 'Nam' || extractedData.gender === 'Nữ') ? extractedData.gender : prev.gender,
                    permanentAddress: extractedData.permanentAddress || prev.permanentAddress,
                    idIssueDate: formatDateForInput(extractedData.idIssueDate) || prev.idIssueDate,
                }));
            };
            reader.onerror = () => {
                throw new Error("Không thể đọc tệp hình ảnh.");
            }
        } catch (error) {
            import('../utils/logger').then(l => l.error('Lỗi khi trích xuất thông tin CCCD:', error));
            setExtractionError("Không thể trích xuất thông tin. Vui lòng thử lại hoặc nhập thủ công.");
        } finally {
            setIsExtracting(false);
            event.target.value = '';
        }
    };
    
    const handleTenantChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setTenantData(prev => ({...prev, [e.target.name]: e.target.value}));
    };

    const handleTenantIdBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const idNumber = e.target.value.trim();
        if (!idNumber) return;

        if (!isNewTenant && tenants.find(t => t.id === currentTenantId)?.idNumber === idNumber) {
            return;
        }

        const existingPerson = allPeople.find(p => p.idNumber === idNumber);
        if (existingPerson) {
            setTenantData(prev => ({
                ...prev,
                name: existingPerson.name || prev.name,
                gender: existingPerson.gender || prev.gender,
                idType: existingPerson.idType || prev.idType,
                phone: existingPerson.phone || prev.phone,
                permanentAddress: existingPerson.permanentAddress || prev.permanentAddress,
                dateOfBirth: existingPerson.dateOfBirth || prev.dateOfBirth,
                idIssueDate: existingPerson.idIssueDate || prev.idIssueDate,
            }));
        }
    };
    
    const handleResidencyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setResidencyData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

     const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setTenantData(prev => ({...prev, documentUrl: reader.result as string}));
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleMemberChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const updatedMembers = [...members];
        const field = e.target.name as keyof RoomMember;
        updatedMembers[index] = {...updatedMembers[index], [field]: e.target.value};
        setMembers(updatedMembers);
    };

    const handleMemberIdBlur = (index: number, e: React.FocusEvent<HTMLInputElement>) => {
        const idNumber = e.target.value.trim();
        if (!idNumber) return;
        
        const originalMember = roomMembers.find(rm => rm.id === members[index].id);
        if (originalMember && originalMember.idNumber === idNumber) {
            return;
        }

        const existingPerson = allPeople.find(p => p.idNumber === idNumber);
        if (existingPerson) {
            const updatedMembers = [...members];
            const currentMember = updatedMembers[index];
            updatedMembers[index] = {
                ...currentMember,
                name: existingPerson.name || currentMember.name,
                gender: existingPerson.gender || currentMember.gender,
                idType: existingPerson.idType || currentMember.idType,
                phone: existingPerson.phone || currentMember.phone,
                permanentAddress: existingPerson.permanentAddress || currentMember.permanentAddress,
                dateOfBirth: existingPerson.dateOfBirth || currentMember.dateOfBirth,
                idIssueDate: existingPerson.idIssueDate || currentMember.idIssueDate,
            };
            setMembers(updatedMembers);
        }
    };

    const removeMember = (id: string, index: number) => {
        setMembers(members.filter((_, i) => i !== index));
        if (!id.startsWith('M_new')) {
            setDeletedMembers([...deletedMembers, id]);
        }
    };

    const handleSaveClick = () => {
        if (rooms.some(r => r.id !== room.id && r.roomNumber.trim().toLowerCase() === roomNumber.trim().toLowerCase())) {
            alert(`Lỗi: Số phòng "${roomNumber}" đã tồn tại. Vui lòng chọn một số phòng khác.`);
            return;
        }

        const proceedWithSave = (finalStatus: RoomStatus) => {
             const updatedRoom: Room = {
                ...room,
                roomNumber: roomNumber,
                price: roomPrice,
                status: finalStatus,
                lastElectricityReading: lastReading,
                currentElectricityReading: currentReading,
            };

            let payload: Parameters<typeof onSave>[0];

            if (tenantData.name.trim() && finalStatus === RoomStatus.Occupied) {
                const tenantId = currentTenantId || `T${Date.now()}`;
                updatedRoom.tenantId = tenantId;

                const finalTenant: Tenant = { ...tenantData, id: tenantId, roomId: room.id };
                const finalMembers = members.map(m => ({ ...m, tenantId: tenantId }));
                const finalResidency: ResidencyRegistration = {
                    ...residencyData,
                    id: residencyRegistrations.find(r => r.personId === tenantId)?.id || `RES${Date.now()}`,
                    personId: tenantId,
                };

                payload = { room: updatedRoom, tenant: finalTenant, members: finalMembers, deletedMembers, isNewTenant, residency: finalResidency };
            } else {
                updatedRoom.tenantId = undefined; // Ensure no tenant is linked if status is not Occupied
                payload = { room: updatedRoom, members: [], deletedMembers: [], isNewTenant: false };
            }
            onSave(payload);
        };

        if (room.status === RoomStatus.Occupied && (roomStatus === RoomStatus.Available || roomStatus === RoomStatus.Maintenance)) {
            setConfirmProps({
                title: 'Xác nhận thay đổi trạng thái',
                message: `Việc đổi trạng thái phòng sang "${roomStatus === RoomStatus.Available ? 'Còn trống' : 'Bảo trì'}" sẽ xóa toàn bộ thông tin khách thuê hiện tại khỏi phòng này. Bạn có chắc chắn?`,
                onConfirm: () => {
                    onClearOccupantAndUpdateRoom(room.id, room.tenantId!, {
                        status: roomStatus,
                        price: roomPrice,
                        roomNumber: roomNumber
                    });
                }
            });
            setConfirmOpen(true);
            return;
        }
        
        if (room.status !== RoomStatus.Occupied && roomStatus === RoomStatus.Occupied) {
             if (!tenantData.name.trim()) {
                alert("Để chuyển trạng thái sang 'Đã thuê', bạn phải nhập thông tin chủ phòng ở tab 'Chủ phòng'.");
                setActiveTab('tenant');
                return;
            }
        }
        
        let finalStatus = roomStatus;
        if (tenantData.name.trim()) {
            finalStatus = RoomStatus.Occupied;
        } else if (room.status === RoomStatus.Occupied) {
             alert("Không thể xóa tên chủ phòng từ đây. Vui lòng sử dụng nút 'Xóa khách thuê' hoặc thay đổi trạng thái phòng.");
             return;
        }

        proceedWithSave(finalStatus);
    };


    const handleClearClick = () => {
        if (room.tenantId) {
             setConfirmProps({
                title: 'Xác nhận Xóa khách thuê',
                message: 'Bạn có chắc chắn muốn xóa toàn bộ thông tin khách thuê, thành viên, và đăng ký cư trú liên quan khỏi phòng này?',
                onConfirm: () => onClearOccupant(room.id, room.tenantId!)
            });
            setConfirmOpen(true);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 sm:p-0">
            {isSelectPersonModalOpen && (
                <SelectPersonModal
                    isOpen={isSelectPersonModalOpen}
                    onClose={() => setIsSelectPersonModalOpen(false)}
                    people={selectablePeople}
                    onSelect={handleSelectPerson}
                    onAddNew={handleAddNewBlankMember}
                />
            )}
             <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    confirmProps.onConfirm();
                    setConfirmOpen(false);
                }}
                title={confirmProps.title}
            >
                {confirmProps.message}
            </ConfirmationModal>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl h-full sm:h-[95vh] flex flex-col">
                <datalist id="relationship-options">
                    <option value="Vý" />
                    <option value="Cháng" />
                    <option value="Con gái" />
                    <option value="Con trai" />
                    <option value="Bá" />
                    <option value="Má" />
                    <option value="Ông" />
                    <option value="Bà" />
                    <option value="Anh trai" />
                    <option value="Chá gái" />
                    <option value="Em trai" />
                    <option value="Em gái" />
                    <option value="Anh rá" />
                    <option value="Em rá" />
                    <option value="Anh" />
                    <option value="Chá" />
                    <option value="Em" />
                    <option value="Cô chú" />
                    <option value="Bác trai" />
                    <option value="Bác gái" />
                    <option value="Cháu" />
                    <option value="Cháu gái" />
                    <option value="Cháu trai" />
                    <option value="Cháu trong" />
                    <option value="Cháu ngoái" />
                    <option value="Má vá" />
                    <option value="Má cháng" />
                    <option value="Bá vá" />
                    <option value="Bá cháng" />
                    <option value="Bác gái" />
                    <option value="Bác trai" />
                    <option value="Dì" />
                    <option value="Cáu" />
                    <option value="Hàng xóm" />
                    <option value="Ban thân" />
                    <option value="Nguyén quen" />
                    <option value="Dáng nghiáp" />
                    <option value="Khách thuê" />
                    <option value="Nguyén cùng phòng" />
                    <option value="Khác" />
                    <option value="Không rõ" />
                </datalist>

                {/* Header */}
                <div className="flex-shrink-0 p-4 sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Quản lý phòng {room.roomNumber}</h2>
                </div>

                {/* Tabs */}
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-2 sm:px-4">
                    <ul className="flex flex-nowrap overflow-x-auto -mb-px text-sm font-medium text-center">
                        <li className="mr-2"><button onClick={() => setActiveTab('tenant')} className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'tenant' ? 'border-primary-500 text-primary-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Chủ phòng</button></li>
                        <li className="mr-2"><button onClick={() => setActiveTab('members')} className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'members' ? 'border-primary-500 text-primary-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Thành viên</button></li>
                        <li className="mr-2"><button onClick={() => setActiveTab('residency')} className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'residency' ? 'border-primary-500 text-primary-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Cư trú</button></li>
                        <li className="mr-2"><button onClick={() => setActiveTab('roomInfo')} className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'roomInfo' ? 'border-primary-500 text-primary-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>Thông tin phòng</button></li>
                    </ul>
                </div>
                
                {/* Scrollable Content */}
                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                   {activeTab === 'tenant' && (
                        <div className="space-y-4">
                            <h3 className="font-semibold text-lg">Thông tin Chủ phòng</h3>
                             <div>
                                <label
                                    htmlFor={hasApiKey ? "id-card-upload-room" : undefined}
                                    className={`w-full flex items-center justify-center px-4 py-2 sm:py-3 border-2 border-dashed rounded-lg ${
                                        hasApiKey 
                                        ? 'cursor-pointer text-primary-600 dark:text-primary-400 border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                                        : 'cursor-not-allowed text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700'
                                    } transition-colors`}
                                    title={!hasApiKey ? "Vui lòng nhập Gemini API Key trong phần Cài đặt (biểu tượng bánh răng ở góc trên bên phải) để sử dụng tính năng này." : "Quét CCCD để điền tự động"}
                                >
                                     {isExtracting ? (
                                        <>
                                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Đang trích xuất...</span>
                                        </>
                                    ) : (
                                        <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        <span className="text-sm sm:text-base">Quét CCCD & điền tự động</span>
                                        </>
                                    )}
                                </label>
                                <input
                                    type="file"
                                    id="id-card-upload-room"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleIdCardUpload}
                                    capture="environment"
                                    disabled={!hasApiKey}
                                />
                                 {extractionError && <p className="mt-2 text-sm text-red-500">{extractionError}</p>}
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <input type="text" name="name" value={tenantData.name} onChange={handleTenantChange} placeholder="Họ và tên" className="input-field col-span-2" required />
                                <select name="gender" value={tenantData.gender} onChange={handleTenantChange} className="input-field">
                                    <option value="Nam">Nam</option>
                                    <option value="Nữ">Nữ</option>
                                    <option value="Khác">Khác</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <select name="idType" value={tenantData.idType} onChange={handleTenantChange} className="input-field">
                                    <option value="CCCD">CCCD</option>
                                    <option value="CMND">CMND</option>
                                    <option value="Passport">Hộ chiếu</option>
                                </select>
                                <input type="text" name="idNumber" value={tenantData.idNumber} onChange={handleTenantChange} onBlur={handleTenantIdBlur} placeholder="Số CMND/CCCD" className="input-field md:col-span-2" required />
                            </div>
                            <input type="tel" name="phone" value={tenantData.phone} onChange={handleTenantChange} placeholder="Số điện thoại" className="input-field" required />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><div><label className="label-field">Ngày sinh</label><input type="date" name="dateOfBirth" value={tenantData.dateOfBirth} onChange={handleTenantChange} className="input-field" required /></div><div><label className="label-field">Ngày cấp CMND/CCCD</label><input type="date" name="idIssueDate" value={tenantData.idIssueDate} onChange={handleTenantChange} className="input-field" required /></div></div>
                            <input type="text" name="permanentAddress" value={tenantData.permanentAddress} onChange={handleTenantChange} placeholder="Địa chỉ thường trú" className="input-field" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><div><label className="label-field">Ngày bắt đầu HĐ</label><input type="date" name="contractStartDate" value={tenantData.contractStartDate} onChange={handleTenantChange} className="input-field" required /></div><div><label className="label-field">Ngày kết thúc HĐ</label><input type="date" name="contractEndDate" value={tenantData.contractEndDate} onChange={handleTenantChange} className="input-field" required /></div></div>
                            <div>
                                <label className="label-field">Giấy tờ tùy thân (Ảnh/PDF)</label>
                                <input type="file" accept="image/*,.pdf" onChange={handleFileChange} className="input-field file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"/>
                                {tenantData.documentUrl && (
                                    <a href={tenantData.documentUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline mt-2 inline-block">Xem giấy tờ đã tải lên</a>
                                )}
                            </div>
                        </div>
                   )}
                   {activeTab === 'members' && (
                       <div className="space-y-4">
                           <h3 className="font-semibold text-lg">Thành viên cùng phòng</h3>
                           {members.map((member, index) => (
                               <div key={member.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                   <div className="flex justify-between items-center cursor-pointer" onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}>
                                       <p className="font-semibold text-gray-800 dark:text-white">{member.name || 'Thành viên mới'}</p>
                                       <div className="flex items-center space-x-4">
                                            <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">{expandedMember === member.id ? 'Thu gọn' : 'Chi tiết'}</span>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); removeMember(member.id, index); }} className="text-red-500 hover:text-red-700 font-semibold">Xóa</button>
                                       </div>
                                   </div>
                                   {expandedMember === member.id && (
                                       <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-3">
                                            <div className="grid grid-cols-3 gap-3">
                                                <input type="text" name="name" value={member.name} onChange={(e) => handleMemberChange(index, e)} placeholder="Họ và tên" className="input-field col-span-2" required />
                                                <select name="gender" value={member.gender} onChange={(e) => handleMemberChange(index, e)} className="input-field">
                                                    <option value="Nam">Nam</option>
                                                    <option value="Nữ">Nữ</option>
                                                    <option value="Khác">Khác</option>
                                                </select>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <select name="idType" value={member.idType} onChange={(e) => handleMemberChange(index, e)} className="input-field">
                                                    <option value="CCCD">CCCD</option>
                                                    <option value="CMND">CMND</option>
                                                    <option value="Passport">Hộ chiếu</option>
                                                </select>
                                                <input type="text" name="idNumber" value={member.idNumber} onChange={(e) => handleMemberChange(index, e)} onBlur={(e) => handleMemberIdBlur(index, e)} placeholder="Số CMND/CCCD" className="input-field md:col-span-2" required />
                                            </div>
                                            <input type="tel" name="phone" value={member.phone} onChange={(e) => handleMemberChange(index, e)} placeholder="Số điện thoại" className="input-field" />
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div><label className="label-field text-xs">Ngày sinh</label><input type="date" name="dateOfBirth" value={member.dateOfBirth} onChange={(e) => handleMemberChange(index, e)} className="input-field"/></div>
                                                <div><label className="label-field text-xs">Ngày cấp CMND/CCCD</label><input type="date" name="idIssueDate" value={member.idIssueDate} onChange={(e) => handleMemberChange(index, e)} className="input-field"/></div>
                                            </div>
                                            <input type="text" name="permanentAddress" value={member.permanentAddress} onChange={(e) => handleMemberChange(index, e)} placeholder="Địa chỉ thường trú" className="input-field" />
                                            <input type="text" name="relationship" value={member.relationship} onChange={(e) => handleMemberChange(index, e)} placeholder="Quan hệ với chủ phòng" className="input-field" list="relationship-options"/>
                                       </div>
                                   )}
                               </div>
                           ))}
                           <button onClick={() => setIsSelectPersonModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-500">Thêm thành viên</button>
                       </div>
                   )}
                   {activeTab === 'residency' && (
                        <div className="space-y-4">
                            <h3 className="font-semibold text-lg">Thông tin Đăng ký Cư trú (Chủ phòng)</h3>
                            <div>
                                <label className="label-field">Tình trạng</label>
                                <select name="status" value={residencyData.status} onChange={handleResidencyChange} className="input-field" required>
                                    {Object.values(ResidencyStatus).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                             <div>
                                <label className="label-field">Địa chỉ tạm trú</label>
                                <input type="text" name="temporaryAddress" value={residencyData.temporaryAddress} onChange={handleResidencyChange} className="input-field"/>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Ngày đến</label>
                                    <input type="date" name="arrivalDate" value={residencyData.arrivalDate} onChange={handleResidencyChange} className="input-field" required/>
                                </div>
                                <div>
                                    <label className="label-field">Ngày đi (dự kiến)</label>
                                    <input type="date" name="departureDate" value={residencyData.departureDate} onChange={handleResidencyChange} className="input-field" required/>
                                </div>
                            </div>
                        </div>
                   )}
                   {activeTab === 'roomInfo' && (
                       <div className="space-y-4">
                           <h3 className="font-semibold text-lg">Thông tin phòng & Điện</h3>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                               <div>
                                    <label className="label-field">Số phòng</label>
                                    <input type="text" value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} className="input-field" required />
                               </div>
                               <div>
                                    <label className="label-field">Tình trạng phòng</label>
                                    <select value={roomStatus} onChange={(e) => setRoomStatus(e.target.value as RoomStatus)} className="input-field" disabled={!tenantData.name.trim() && room.status === RoomStatus.Occupied}>
                                         {room.status !== RoomStatus.Occupied ? (
                                            <>
                                                <option value={RoomStatus.Available}>Còn trống</option>
                                                <option value={RoomStatus.Maintenance}>Bảo trì</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value={RoomStatus.Occupied}>Đã thuê</option>
                                                <option value={RoomStatus.Available}>Chuyển thành: Còn trống (Xóa khách thuê)</option>
                                                <option value={RoomStatus.Maintenance}>Chuyển thành: Bảo trì (Xóa khách thuê)</option>
                                            </>
                                        )}
                                    </select>
                               </div>
                           </div>
                           <div>
                                <label className="label-field">Giá phòng (VNĐ)</label>
                                <input type="number" value={roomPrice} onChange={(e) => setRoomPrice(Number(e.target.value))} className="input-field"/>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Số điện cũ (Chốt kỳ trước)</label>
                                    <input type="number" value={lastReading} onChange={(e) => setLastReading(Number(e.target.value))} className="input-field"/>
                                </div>
                                <div>
                                    <label className="label-field">Số điện mới (Hiện tại)</label>
                                    <input type="number" value={currentReading} onChange={(e) => setCurrentReading(Number(e.target.value))} min={lastReading} className="input-field"/>
                                </div>
                           </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                Lưu ý: Chỉnh sửa các chỉ số điện tại đây sẽ ảnh hưởng trực tiếp đến hóa đơn. Chức năng này dùng để điều chỉnh sai sót trong quá trình nhập liệu.
                            </p>
                       </div>
                   )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 flex justify-between items-center p-4 sm:p-6 mt-auto border-t border-gray-200 dark:border-gray-700">
                    <div>
                        {room.status === RoomStatus.Occupied && (
                            <button onClick={handleClearClick} className="px-4 py-2 font-medium text-white bg-red-600 rounded-lg hover:bg-red-500">Xóa khách thuê</button>
                        )}
                    </div>
                    <div className="flex space-x-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Hủy</button>
                        <button type="button" onClick={handleSaveClick} className="px-4 py-2 font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500">Lưu thay đổi</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const RoomManagement: React.FC<RoomManagementProps> = ({ rooms, setRooms, tenants, setTenants, roomMembers, setRoomMembers, residencyRegistrations, setResidencyRegistrations, allPeople, geminiApiKey, boardingHouseInfo }) => {
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedRoomForInvoice, setSelectedRoomForInvoice] = useState<Room | null>(null);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [roomToManage, setRoomToManage] = useState<Room | null>(null);

     // Confirmation Modal State
    const [isConfirmOpen, setConfirmOpen] = useState(false);
    const [confirmProps, setConfirmProps] = useState({ title: '', message: '', onConfirm: () => {} });

    const getTenantName = (tenantId?: string): string => tenants.find(t => t.id === tenantId)?.name || 'N/A';
    
    const handleElectricityChange = (roomId: string, value: string) => {
        const newReading = parseInt(value, 10);
        if (isNaN(newReading) || newReading < 0) return;
        setRooms(currentRooms => currentRooms.map(r => r.id === roomId ? { ...r, currentElectricityReading: newReading } : r));
    };

    const handleTotalBillChange = (roomId: string, newTotalString: string) => {
        const newTotal = parseInt(newTotalString, 10);
        if (isNaN(newTotal) || newTotal < 0) return;

        setRooms(currentRooms =>
            currentRooms.map(r => {
                if (r.id === roomId) {
                    if (r.status === RoomStatus.Occupied) {
                        const electricityUsage = r.currentElectricityReading - r.lastElectricityReading;
                        const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
                        const newPrice = newTotal - electricityCost;
                        return { ...r, price: newPrice >= 0 ? newPrice : 0 };
                    } else {
                        // For available/maintenance rooms, total bill is just the price
                        return { ...r, price: newTotal };
                    }
                }
                return r;
            })
        );
    };

    const handleFinalizeElectricity = (roomId: string) => {
        setConfirmProps({
            title: 'Xác nhận chốt số điện',
            message: 'Bạn có chắc chắn muốn chốt số điện? Số điện mới sẽ trở thành số điện cũ cho kỳ tiếp theo.',
            onConfirm: () => {
                 setRooms(currentRooms => 
                    currentRooms.map(r => 
                        r.id === roomId ? { ...r, lastElectricityReading: r.currentElectricityReading } : r
                    )
                );
            }
        });
        setConfirmOpen(true);
    };
    
    const handleResetElectricity = (roomId: string) => {
        setRooms(currentRooms => 
            currentRooms.map(r => 
                r.id === roomId ? { ...r, currentElectricityReading: r.lastElectricityReading } : r
            )
        );
    };

    const handleOpenInvoice = (room: Room) => {
        setSelectedRoomForInvoice(room);
        setIsInvoiceModalOpen(true);
    };

    const handleOpenManageModal = (room: Room) => {
        setRoomToManage(room);
        setIsManageModalOpen(true);
    };

    const handleCloseManageModal = () => {
        setRoomToManage(null);
        setIsManageModalOpen(false);
    };

    const handleSaveOccupants = (payload: { room: Room, tenant?: Tenant, members: RoomMember[], deletedMembers: string[], isNewTenant: boolean, residency?: ResidencyRegistration }) => {
        const { room, tenant, members, deletedMembers, isNewTenant, residency } = payload;
        
        // Update room
        setRooms(prev => prev.map(r => r.id === room.id ? room : r));

        // Update tenant
        if (tenant) {
            if (isNewTenant) {
                setTenants(prev => [...prev, tenant]);
            } else {
                setTenants(prev => prev.map(t => t.id === tenant.id ? tenant : t));
            }
        }

        // Update members
        setRoomMembers(prev => {
            const others = prev.filter(m => !deletedMembers.includes(m.id) && m.tenantId !== tenant?.id);
            return [...others, ...members];
        });

        // Update residency
        if (residency) {
            setResidencyRegistrations(prev => {
                const isExisting = prev.some(r => r.id === residency.id);
                if (isExisting) {
                    return prev.map(r => r.id === residency.id ? residency : r);
                }
                return [...prev, residency];
            });
        }

        handleCloseManageModal();
    };

    const handleClearOccupant = (roomId: string, tenantId: string) => {
        if (!tenantId) return;
        const membersToDelete = roomMembers.filter(m => m.tenantId === tenantId);
        const peopleIdsToDelete = [tenantId, ...membersToDelete.map(m => m.id)];

        setRooms(prev => prev.map(r => r.id === roomId ? { ...r, status: RoomStatus.Available, tenantId: undefined } : r));
        setTenants(prev => prev.filter(t => t.id !== tenantId));
        setRoomMembers(prev => prev.filter(m => m.tenantId !== tenantId));
        setResidencyRegistrations(prev => prev.filter(reg => !peopleIdsToDelete.includes(reg.personId)));
        handleCloseManageModal();
    };

    const handleClearOccupantAndUpdateRoom = (roomId: string, tenantId: string, updatedRoomData: { status: RoomStatus; price: number; roomNumber: string }) => {
        if (!tenantId) return;
        const membersToDelete = roomMembers.filter(m => m.tenantId === tenantId);
        const peopleIdsToDelete = [tenantId, ...membersToDelete.map(m => m.id)];

        setRooms(prev => prev.map(r => {
            if (r.id === roomId) {
                return {
                    ...r,
                    status: updatedRoomData.status,
                    price: updatedRoomData.price,
                    roomNumber: updatedRoomData.roomNumber,
                    tenantId: undefined,
                };
            }
            return r;
        }));
        setTenants(prev => prev.filter(t => t.id !== tenantId));
        setRoomMembers(prev => prev.filter(m => m.tenantId !== tenantId));
        setResidencyRegistrations(prev => prev.filter(reg => !peopleIdsToDelete.includes(reg.personId)));
        handleCloseManageModal();
    };

    const getOccupantCount = (tenantId?: string) => {
        if (!tenantId) return 0;
        const membersCount = roomMembers.filter(m => m.tenantId === tenantId).length;
        return 1 + membersCount; // 1 for the main tenant
    };


    return (
        <>
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    confirmProps.onConfirm();
                    setConfirmOpen(false);
                }}
                title={confirmProps.title}
            >
                {confirmProps.message}
            </ConfirmationModal>
            <div className="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Danh sách phòng</h2>
                </div>
                
                {/* Desktop Table */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-4 py-3">Số phòng</th>
                                <th scope="col" className="px-4 py-3">Chủ phòng</th>
                                <th scope="col" className="px-4 py-3">Số người</th>
                                <th scope="col" className="px-4 py-3">Tình trạng</th>
                                <th scope="col" className="px-4 py-3">Số điện mới</th>
                                <th scope="col" className="px-4 py-3">Tiền điện (VNĐ)</th>
                                <th scope="col" className="px-4 py-3">Tổng cộng (VNĐ)</th>
                                <th scope="col" className="px-4 py-3 text-right">Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rooms.map(room => {
                                const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
                                const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
                                const totalBill = room.status === RoomStatus.Occupied ? room.price + electricityCost : room.price;
                                const occupantCount = getOccupantCount(room.tenantId);
                                const statusInfo = {
                                    [RoomStatus.Available]: { text: 'Còn trống', color: '#059669', bg: '#D1FAE5'},
                                    [RoomStatus.Occupied]: { text: 'Đã thuê', color: '#1D4ED8', bg: '#DBEAFE'},
                                    [RoomStatus.Maintenance]: { text: 'Bảo trì', color: '#D97706', bg: '#FEF3C7'},
                                }
                                return (
                                <tr key={room.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                    <td className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{room.roomNumber}</td>
                                    <td className="px-4 py-4">{getTenantName(room.tenantId)}</td>
                                    <td className="px-4 py-4 font-medium text-center">{occupantCount > 0 ? occupantCount : 'N/A'}</td>
                                    <td className="px-4 py-4">
                                        <span className="px-2 py-1 text-xs font-semibold rounded-full" style={{ color: statusInfo[room.status].color, backgroundColor: statusInfo[room.status].bg}}>
                                            {statusInfo[room.status].text}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="relative">
                                            <input 
                                                type="number" 
                                                value={room.currentElectricityReading} 
                                                onChange={(e) => handleElectricityChange(room.id, e.target.value)}
                                                min={room.lastElectricityReading}
                                                className="w-24 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white pr-8"
                                                disabled={room.status !== RoomStatus.Occupied}
                                            />
                                             {room.currentElectricityReading > room.lastElectricityReading && (
                                                <button onClick={() => handleResetElectricity(room.id)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1" title="Reset số điện">
                                                    <Icon name="refresh" className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">{electricityCost.toLocaleString()}</td>
                                    <td className="px-4 py-4 font-semibold">
                                        <input
                                            type="number"
                                            value={totalBill}
                                            onChange={(e) => handleTotalBillChange(room.id, e.target.value)}
                                            className="w-32 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        />
                                    </td>
                                    <td className="px-4 py-4 text-right space-x-2">
                                        <button onClick={() => handleOpenManageModal(room)} className="font-medium text-primary-600 dark:text-primary-500 hover:underline">Quản lý</button>
                                         {room.status === RoomStatus.Occupied && (
                                            <>
                                            <button onClick={() => handleFinalizeElectricity(room.id)} className="font-medium text-yellow-600 dark:text-yellow-500 hover:underline">
                                                Chốt số
                                            </button>
                                            <button onClick={() => handleOpenInvoice(room)} className="font-medium text-green-600 dark:text-green-500 hover:underline">
                                                Hóa Đơn
                                            </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )})}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
                    {rooms.map(room => {
                        const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
                        const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
                        const totalBill = room.status === RoomStatus.Occupied ? room.price + electricityCost : room.price;
                        const occupantCount = getOccupantCount(room.tenantId);
                        const statusInfo = {
                            [RoomStatus.Available]: { text: 'Còn trống', color: '#059669', bg: '#D1FAE5'},
                            [RoomStatus.Occupied]: { text: 'Đã thuê', color: '#1D4ED8', bg: '#DBEAFE'},
                            [RoomStatus.Maintenance]: { text: 'Bảo trì', color: '#D97706', bg: '#FEF3C7'},
                        };
                        return (
                            <div key={room.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-bold text-lg text-gray-900 dark:text-white">Phòng {room.roomNumber}</p>
                                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            <span>{getTenantName(room.tenantId)}</span>
                                            {occupantCount > 0 && (
                                                <span className="ml-3 flex items-center text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                                    <Icon name="tenants" className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                                                    {occupantCount}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="px-2 py-1 text-xs font-semibold rounded-full" style={{ color: statusInfo[room.status].color, backgroundColor: statusInfo[room.status].bg}}>
                                        {statusInfo[room.status].text}
                                    </span>
                                </div>
                                
                                {room.status === RoomStatus.Occupied && (
                                    <div>
                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Số điện mới</label>
                                        <div className="relative mt-1">
                                            <input 
                                                type="number" 
                                                value={room.currentElectricityReading} 
                                                onChange={(e) => handleElectricityChange(room.id, e.target.value)}
                                                min={room.lastElectricityReading}
                                                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white pr-8"
                                            />
                                            {room.currentElectricityReading > room.lastElectricityReading && (
                                                <button onClick={() => handleResetElectricity(room.id)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1" title="Reset số điện">
                                                    <Icon name="refresh" className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Tiền điện:</span>
                                        <span className="font-medium text-gray-700 dark:text-gray-200">{electricityCost.toLocaleString()} VNĐ</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400 font-bold text-base">Tổng cộng:</span>
                                         <input
                                            type="number"
                                            value={totalBill}
                                            onChange={(e) => handleTotalBillChange(room.id, e.target.value)}
                                            className="w-36 font-bold text-base text-primary-600 dark:text-primary-400 bg-gray-50 border border-gray-300 text-right rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2 dark:bg-gray-700 dark:border-gray-600"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex justify-end space-x-3 pt-2">
                                    <button onClick={() => handleOpenManageModal(room)} className="px-3 py-1 text-sm font-medium text-primary-600 dark:text-primary-500 bg-primary-100 dark:bg-primary-900/50 rounded-md hover:bg-primary-200 dark:hover:bg-primary-900">Quản lý</button>
                                    {room.status === RoomStatus.Occupied && (
                                        <>
                                            <button onClick={() => handleFinalizeElectricity(room.id)} className="px-3 py-1 text-sm font-medium text-yellow-600 dark:text-yellow-500 bg-yellow-100 dark:bg-yellow-900/50 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-900">Chốt số</button>
                                            <button onClick={() => handleOpenInvoice(room)} className="px-3 py-1 text-sm font-medium text-green-600 dark:text-green-500 bg-green-100 dark:bg-green-900/50 rounded-md hover:bg-green-200 dark:hover:bg-green-900">Hóa Đơn</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {isInvoiceModalOpen && <InvoiceModal room={selectedRoomForInvoice} tenantName={getTenantName(selectedRoomForInvoice?.tenantId)} onClose={() => setIsInvoiceModalOpen(false)} boardingHouseInfo={boardingHouseInfo} />}
            {isManageModalOpen && roomToManage && 
                <ManageRoomModal 
                    room={roomToManage}
                    rooms={rooms}
                    tenants={tenants}
                    roomMembers={roomMembers}
                    residencyRegistrations={residencyRegistrations}
                    onClose={handleCloseManageModal}
                    onSave={handleSaveOccupants}
                    onClearOccupant={handleClearOccupant}
                    onClearOccupantAndUpdateRoom={handleClearOccupantAndUpdateRoom}
                    allPeople={allPeople}
                    geminiApiKey={geminiApiKey}
                    boardingHouseInfo={boardingHouseInfo}
                />}
        </>
    );
};

export default RoomManagement;
